import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SLIDESHOWMODALSTEP, COLOR, ALERT } from 'config/constants';
import { useAlert, useListing } from 'hooks';
import { HvModal, HvButton } from 'components';
import { Picture, VideoTemplate } from 'models';
import { PictureSelection } from './components';

const useStyles = makeStyles({
  title: {
    fontWeight: 'bold',
  },
  description: {
    fontSize: 14,
    marginBottom: 16,
    color: COLOR.DARK_BLUE,
  },
  cancelButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginBottom: 5,
  },
  reminderContent: {
    backgroundColor: COLOR.LIGHT_RED_CONTENT,
    padding: 10,
    borderRadius: 8,
    marginBottom: 10,
  },
  reminderText: {
    color: COLOR.RED,
  },
  tabs: {
    marginBottom: 20,
    flexGrow: 1,
  },
  tab: {
    fontSize: 12,
    textTransform: 'capitalize',
    width: '50%',
  },
});
interface Props {
  setActiveStep: (_: number) => void;
  selectedPictures: Picture[];
  setSelectedPictures: (_: Picture[]) => void;
  picturesWithNewOrder: Picture[];
  setPicturesWithNewOrder: (_: any) => void;
  selectedTemplate: VideoTemplate;
}
function SlideshowPictures({
  setActiveStep,
  selectedPictures,
  setSelectedPictures,
  picturesWithNewOrder,
  setPicturesWithNewOrder,
  selectedTemplate,
}: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const path = window.location.pathname;
  const arr = path.split('/');
  const { displayAlert } = useAlert();
  const listing_token = arr[arr.length - 1];
  const { listing } = useListing(listing_token);

  const fmtSlideshow = intl.formatMessage({ id: 'deliver.video_slideshow.title' });
  const fmtSelectPhotoForTemplate = intl.formatMessage({
    id: 'deliver.video_slideshow.selectPhotoForTemplate',
  });
  const fmtTemplateNeeds = intl.formatMessage({ id: 'deliver.video_slideshow.templateNeeds' });
  const fmtPhotos = intl.formatMessage({ id: 'deliver.video_slideshow.photos' });
  const fmtEnoughPhotos = intl.formatMessage({ id: 'deliver.video_slideshow.enoughPhotos' });

  useEffect(() => {
    const filteredListingPictures = listing.pictures
      .filter((picture: Picture) => {
        return picture.included_in_video === true && picture.distrib_url !== null;
      })
      .sort((a: Picture, b: Picture) => {
        return a.picture_index - b.picture_index;
      })
      .slice(0, selectedTemplate.listing_picture_count);
    setSelectedPictures(filteredListingPictures);
    setPicturesWithNewOrder(filteredListingPictures);
  }, []);
  const onClose = () => {
    setActiveStep(SLIDESHOWMODALSTEP.TEMPLATES);
  };

  const onNext = () => {
    setActiveStep(SLIDESHOWMODALSTEP.SUMMARY);
  };

  const onSelectPicture = (picture: Picture) => {
    const isAllSelected = selectedPictures.length === selectedTemplate.listing_picture_count;
    const index = selectedPictures.findIndex((p) => {
      return p.id === picture.id;
    });
    if (isAllSelected && index < 0) {
      displayAlert(ALERT.ERROR, fmtEnoughPhotos);
    } else if (index >= 0) {
      setSelectedPictures(
        selectedPictures.filter((p) => {
          return p.id !== picture.id;
        }),
      );
      setPicturesWithNewOrder(
        picturesWithNewOrder.filter((p) => {
          return p.id !== picture.id;
        }),
      );
    } else {
      setSelectedPictures([...selectedPictures, picture]);
      setPicturesWithNewOrder([...picturesWithNewOrder, picture]);
    }
  };

  const buttons = (
    <Box>
      <HvButton className={styles.cancelButton} onClick={onClose}>
        {intl.formatMessage({ id: 'deliver.photos.back' })}
      </HvButton>
      <HvButton
        disabled={selectedPictures.length < selectedTemplate.listing_picture_count}
        onClick={onNext}
      >
        {intl.formatMessage({ id: 'deliver.photos.next' })}
      </HvButton>
    </Box>
  );

  return (
    <HvModal title={fmtSlideshow} buttons={buttons} headerImage='slideshow' onClose={onClose}>
      <Typography className={styles.title}>{fmtSelectPhotoForTemplate}</Typography>
      <Typography className={styles.description}>
        {`${fmtTemplateNeeds} ${selectedTemplate.listing_picture_count} ${fmtPhotos}`}
      </Typography>
      <PictureSelection
        selectedPictures={selectedPictures}
        onSelectPicture={onSelectPicture}
        picturesWithNewOrder={picturesWithNewOrder}
        setPicturesWithNewOrder={setPicturesWithNewOrder}
        selectedTemplate={selectedTemplate}
      />
    </HvModal>
  );
}
export default SlideshowPictures;
